import { useState } from "react";

import { t } from "i18next";

import DeleteIcon from "../../shared/Icons/DeleteIcon";
import UploadIcon from "../../shared/Icons/UploadIcon";
import ViewIcon from "../../shared/Icons/ViewIcon";
import AttachmentIcon from "../../shared/Icons/AttachmentIcon";
import Typography from "../../shared/typography/Typography";
import { BannerModal } from "../BannerModal";
import { FileUploadButton } from "../FIleUploadButton";

import { Container, Header, Content, ActionsContainer, FileName} from "./style";
import { getFileSizeStr } from "../utils/getFileSize";
import { breakpointSize } from "../../../theme/mediaQuerie";
import { dashboardApi } from "../../../api/DashboardApi";

export interface IFile {
  file: File;
  name: string;
  url: string;
}

interface IProps {
  file: IFile | null;
  setFile: React.Dispatch<IFile | null>;
  type: 'mobile' | 'desktop';
  maxBannerWidth?: number;
  maxBannerHeight?: number;
  maxBannerSize: number; // in bytes
  id: string;
  channel?: string
}

const isMobile = window.innerWidth <= breakpointSize.Sm;

export const UploadBanner = ({type, maxBannerWidth, maxBannerHeight, maxBannerSize, file, setFile, id, channel }: IProps) => {
  const imageDimension = t('event.dashboard.image.dimensions', 
    {maxImageWidth: maxBannerWidth, maxImageHeight: maxBannerHeight});
  const imageFormat = t('event.dashboard.image.format', {maxImageSize: getFileSizeStr(maxBannerSize)});
  const messageBanner = <> {imageDimension} <br/> {imageFormat} </>;

  const [message, setMessage] = useState(messageBanner);
  const [showModal, setShowModal] = useState(false);
  const [hasError, setHasError] = useState(false);

  const checkFileFormat = (file: File) => {
    const acceptedTypes = ["svg", "png", "jpg", "jpeg"];
    const fileType = file.type.replace("image/", "");
    if (!acceptedTypes.includes(fileType)) {
      setMessage(<>{t('dashboard.image.error.format')!}</>);
      return false;
    }
    return true;
  };

  const checkFileSize = (file: File) => {
    if (file.size > maxBannerSize) {
      setMessage(<>{t('event.dashboard.image.error.size', {maxImageSize: getFileSizeStr(maxBannerSize)})!}</>);
      return false;
    }
    return true;
  };

  const checkFileDimension = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const oFReader = new FileReader();
  
      oFReader.readAsDataURL(file);
  
      oFReader.onload = (event: ProgressEvent<FileReader>) => {
        const image = new Image();
        image.src = event.target?.result as string;
  
        image.onload = function () {
          const width = image.width;
          const height = image.height;
  
          if ((maxBannerWidth && width > maxBannerWidth) || (maxBannerHeight && height > maxBannerHeight)) {
             setMessage(<>{t('dashboard.image.error.dimensions', 
              {maxImageWidth: maxBannerWidth, maxImageHeight: maxBannerHeight})!}</>);
            setHasError(true);
            resolve(false);
          } else {
            resolve(true);
          }
        };
      };
    });
  };

  const handleLoadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if(files){
      const file: File = files[0];
      if(checkFileFormat(file) && checkFileSize(file) && await checkFileDimension(file)) {
        const url = URL.createObjectURL(file);
        setFile({
          url: url,
          name: file.name,
          file: file,
        })
        setMessage(messageBanner);
        setHasError(false);
      }
      else {
        setHasError(true);
      }
    }
  };

  const handleRemoveFile = async () => {
    if(file){
      await dashboardApi.deleteBanner(
      `${file.name}_${type}` || "",
      channel || ""
    );
    }
    setMessage(messageBanner);
    setFile(null);

  };

  return (
    <Container>
      <Header>
        {!file && (
          <>
            <Typography type={isMobile ? "buttonXS" : "paragraphS"} color="title">Banner {type}</Typography>
            <FileUploadButton label={t('attach')} id={id} onChange={handleLoadFile} />
          </>
        )}
      </Header>
      <Content>
        { file && (
          <>
            <FileName>
              <AttachmentIcon />
              <Typography type={isMobile ? "buttonXS" : "paragraphS"} color="title" >{file.name}</Typography>
            </FileName>
            <ActionsContainer>
              <ViewIcon onClick={() => setShowModal(true)}/>
              <UploadIcon handleLoadFile={handleLoadFile} idForUploadButton={id}/>
              <DeleteIcon handleRemoveFile={handleRemoveFile}/>
            </ActionsContainer>
          </>
        )}
        <Typography color={hasError ? "error" : "body"} type="paragraphXS" >{message}</Typography>
      </Content>
      { (showModal && file) && <BannerModal setShowModal={setShowModal} src={file.url}/> }
    </Container>
  );
}